import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { Group } from '../../../core/models/dashboard.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-users-by-group',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, LoadingSpinnerComponent],
  templateUrl: './users-by-group.component.html'
})
export class UsersByGroupComponent implements OnInit, OnDestroy {
  public groups: Group[] = [];
  public loading = false;
  public error = false;
  public chartOptions: any;
  public hasData = false;
  public hasGroups = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService
  ) {
    // Não inicializa o gráfico aqui - será inicializado apenas quando houver dados
  }

  ngOnInit(): void {
    this.loadUsersByGroup();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadUsersByGroup(): void {
    // Verificar se o usuário está autenticado
    if (!this.authService.isAuthenticated()) {
      console.warn('Usuário não autenticado. Não é possível carregar usuários por grupo.');
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.dashboardService.getUsersByGroup()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.groups = response.data;
            this.hasData = false;
            this.hasGroups = response.data.length > 0;
            
            // Só atualiza o gráfico se houver grupos E se houver dados relevantes
            if (response.data.length > 0) {
              // Verifica se pelo menos um grupo tem usuários
              const hasUsersInGroups = response.data.some(group => group.users_count > 0);
              
              if (hasUsersInGroups) {
                this.hasData = true;
                this.updateChartData(response.data);
              }
            }
            
            this.loading = false;
          } else {
            console.warn('Resposta da API sem dados válidos:', response);
            this.hasData = false;
            this.hasGroups = false;
            this.error = true;
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Erro ao carregar usuários por grupo:', error);
          this.hasData = false;
          this.hasGroups = false;
          this.handleApiError(error);
          this.loading = false;
        }
      });
  }

  /**
   * Atualiza os dados do gráfico com base nos grupos recebidos
   * @param groups Lista de grupos da API
   */
  private updateChartData(groups: Group[]): void {
    // Ordenar grupos por nome (G1, G2, G3, G4, G5, G6)
    const sortedGroups = this.sortGroupsByName(groups);
    
    const data = sortedGroups.map(group => group.users_count);
    const categories = sortedGroups.map(group => group.name);
    const colors = sortedGroups.map(group => this.getChartColor(group.color));
    const labelColors = sortedGroups.map(group => this.getChartColor(group.color));

    this.chartOptions = {
      series: [
        {
          name: 'Usuários',
          data: data,
        },
      ],
      chart: {
        height: 335,
        type: 'bar',
        toolbar: {
          show: false,
        },
        events: {
          click: function (chart: any, w: any, e: any) {
            // Possível implementação futura de clique em coluna
          },
        },
      },
      colors: colors,
      plotOptions: {
        bar: {
          columnWidth: '45%',
          distributed: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: labelColors,
            fontSize: '12px',
          },
        },
      },
    };
  }

  /**
   * Ordena os grupos por nome (G1, G2, G3, G4, G5, G6)
   * @param groups Lista de grupos
   * @returns Lista ordenada de grupos
   */
  private sortGroupsByName(groups: Group[]): Group[] {
    return groups.sort((a, b) => {
      // Extrair número do nome do grupo (ex: "Grupo 1" -> 1, "G1" -> 1)
      const getGroupNumber = (name: string): number => {
        const match = name.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : 999; // 999 para grupos sem número
      };

      const numA = getGroupNumber(a.name);
      const numB = getGroupNumber(b.name);
      
      return numA - numB;
    });
  }

  /**
   * Retorna a cor hexadecimal do grupo
   * A API já retorna as cores em formato hexadecimal (ex: '#845ADF')
   * @param color Código hexadecimal da cor
   * @returns Código hexadecimal da cor ou fallback cinza
   */
  private getChartColor(color: string): string {
    // Validar se é um hexadecimal válido
    if (color && /^#[0-9A-F]{6}$/i.test(color)) {
      return color;
    }
    
    // Fallback caso não seja um hex válido
    return '#6b7280';
  }

  /**
   * Trata erros específicos da API
   * @param error Erro recebido da API
   */
  private handleApiError(error: any): void {
    this.error = true;
    
    // Log específico baseado no tipo de erro
    if (error.status === 401) {
      console.warn('Token não fornecido, inválido ou expirado');
    } else if (error.status === 500) {
      console.error('Erro interno do servidor');
    } else {
      console.error('Erro desconhecido ao carregar usuários por grupo:', error);
    }
  }
}

